import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

export interface CalendarDay {
  date: Date | null;
  dayNumber: number | null;
  isSelectedStart: boolean;
  isSelectedEnd: boolean;
  isInRange: boolean;
  isToday: boolean;
  isClickable: boolean;
}

export interface CalendarMonth {
  name: string;
  year: number;
  monthIndex: number;
  days: CalendarDay[];
}

@Component({
  standalone: false,
  selector: 'btx-date-range-picker-dialog',
  templateUrl: './date-range-picker-dialog.component.html',
  styleUrls: ['./date-range-picker-dialog.component.scss']
})
export class BtxDateRangePickerDialogComponent implements OnInit {
  months: CalendarMonth[] = [];
  startDate: Date | null = null;
  endDate: Date | null = null;
  weekDays: string[] = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cts', 'Paz'];

  // Keep track of active current month index for navigation
  baseDate: Date = new Date();

  constructor(
    public dialogRef: MatDialogRef<BtxDateRangePickerDialogComponent>,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data) {
      this.startDate = data.startDate || null;
      this.endDate = data.endDate || null;
    }
  }

  ngOnInit(): void {
    this.generateCalendars();
  }

  generateCalendars(): void {
    const monthsList: CalendarMonth[] = [];
    
    // We show 2 months: baseDate month, and baseDate + 1 month
    for (let i = 0; i < 2; i++) {
      const targetDate = new Date(this.baseDate.getFullYear(), this.baseDate.getMonth() + i, 1);
      const year = targetDate.getFullYear();
      const monthIndex = targetDate.getMonth();
      
      const monthName = targetDate.toLocaleString(this.translate.currentLang || 'tr', { month: 'long' });
      const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      
      const days = this.getMonthDays(year, monthIndex);
      
      monthsList.push({
        name: capitalizedMonth,
        year,
        monthIndex,
        days
      });
    }
    
    this.months = monthsList;
    this.updateSelectionStates();
  }

  getMonthDays(year: number, monthIndex: number): CalendarDay[] {
    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Number of days in the month
    const totalDays = new Date(year, monthIndex + 1, 0).getDate();
    
    // Weekday of the first day (Monday-start: 0 = Mon, ..., 6 = Sun)
    const firstDayOfWeek = (new Date(year, monthIndex, 1).getDay() + 6) % 7;

    // Pad first week
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({
        date: null,
        dayNumber: null,
        isSelectedStart: false,
        isSelectedEnd: false,
        isInRange: false,
        isToday: false,
        isClickable: false
      });
    }

    // Add days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, monthIndex, day);
      date.setHours(0, 0, 0, 0);
      
      days.push({
        date,
        dayNumber: day,
        isSelectedStart: false,
        isSelectedEnd: false,
        isInRange: false,
        isToday: date.getTime() === today.getTime(),
        isClickable: date.getTime() >= today.getTime() // Cannot select past dates
      });
    }

    // Pad last week to complete the grid (multiple of 7)
    const totalCells = days.length;
    const remainingCells = (7 - (totalCells % 7)) % 7;
    for (let i = 0; i < remainingCells; i++) {
      days.push({
        date: null,
        dayNumber: null,
        isSelectedStart: false,
        isSelectedEnd: false,
        isInRange: false,
        isToday: false,
        isClickable: false
      });
    }

    return days;
  }

  onDayClick(day: CalendarDay): void {
    if (!day.isClickable || !day.date) return;

    if (!this.startDate || (this.startDate && this.endDate)) {
      this.startDate = day.date;
      this.endDate = null;
    } else if (this.startDate && !this.endDate) {
      if (day.date < this.startDate) {
        this.startDate = day.date;
      } else {
        this.endDate = day.date;
      }
    }

    this.updateSelectionStates();
  }

  updateSelectionStates(): void {
    this.months.forEach(month => {
      month.days.forEach(day => {
        if (!day.date) return;
        
        const time = day.date.getTime();
        const startTime = this.startDate ? this.startDate.getTime() : null;
        const endTime = this.endDate ? this.endDate.getTime() : null;

        day.isSelectedStart = startTime !== null && time === startTime;
        day.isSelectedEnd = endTime !== null && time === endTime;
        day.isInRange = startTime !== null && endTime !== null && time > startTime && time < endTime;
      });
    });
  }

  navigateMonth(direction: number): void {
    this.baseDate = new Date(this.baseDate.getFullYear(), this.baseDate.getMonth() + direction, 1);
    this.generateCalendars();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  apply(): void {
    if (this.startDate && this.endDate) {
      this.dialogRef.close({
        startDate: this.startDate,
        endDate: this.endDate
      });
    }
  }

  get isApplyDisabled(): boolean {
    return !this.startDate || !this.endDate;
  }

  clear(): void {
    this.startDate = null;
    this.endDate = null;
    this.updateSelectionStates();
  }
}
